import { foodMatchesSearch, normalizeSearchText, searchTokens, validateFoodDefinition } from "./domain";
import {
  deleteCatalogVersion,
  getCatalogMeta,
  putFoodBatch,
  searchStoredFoods,
  setCatalogMeta
} from "./storage";
import type { CatalogManifest, FoodDefinition } from "./types";

export type CatalogProgress = {
  status: "checking" | "downloading" | "installing" | "ready" | "fallback";
  completed: number;
  total: number;
  message: string;
};

let coreFoods: FoodDefinition[] = [];
let activeVersion = "core-1";

export async function initializeCatalog(onProgress?: (progress: CatalogProgress) => void): Promise<void> {
  coreFoods = await loadCoreFoods();
  activeVersion = (await getCatalogMeta<string>("activeCatalog")) ?? "core-1";
  void installLatestCatalog(onProgress).catch((error) => {
    onProgress?.({ status: "fallback", completed: 0, total: 0, message: error instanceof Error ? error.message : "Core catalog active" });
  });
}

export function getActiveCatalogVersion(): string {
  return activeVersion;
}

export async function searchCatalog(query: string, customFoods: FoodDefinition[] = [], limit = 30): Promise<FoodDefinition[]> {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [];
  const local = [...customFoods, ...coreFoods]
    .filter((food) => foodMatchesSearch(food, normalized))
    .slice(0, limit);
  if (activeVersion === "core-1" || local.length >= limit) return dedupe(local).slice(0, limit);
  const stored = await searchStoredFoods(activeVersion, normalized, limit - local.length);
  return dedupe([...local, ...stored]).slice(0, limit);
}

export async function searchOnlineBrandedFoods(query: string): Promise<FoodDefinition[]> {
  const normalized = normalizeSearchText(query);
  if (normalized.length < 2) return [];
  const response = await fetch(`/api/foods/search?q=${encodeURIComponent(normalized)}`, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(response.status === 503 ? "Online food search is not configured yet." : "Online food search failed.");
  const payload = (await response.json()) as { foods?: unknown[] };
  return (payload.foods ?? []).filter(validateFoodDefinition) as FoodDefinition[];
}

async function loadCoreFoods(): Promise<FoodDefinition[]> {
  const response = await fetch("/food-data/core-foods.json");
  if (!response.ok) throw new Error("The built-in food catalog could not be loaded.");
  const rows = (await response.json()) as unknown[];
  return rows.filter(validateFoodDefinition) as FoodDefinition[];
}

async function installLatestCatalog(onProgress?: (progress: CatalogProgress) => void): Promise<void> {
  onProgress?.({ status: "checking", completed: 0, total: 0, message: "Checking food catalog…" });
  const manifestResponse = await fetch("/food-data/catalog-manifest.json", { cache: "no-cache" });
  if (!manifestResponse.ok) throw new Error("Core foods available; full catalog will retry online.");
  const manifest = (await manifestResponse.json()) as CatalogManifest;
  validateManifest(manifest);
  if (activeVersion === manifest.catalogVersion) {
    onProgress?.({ status: "ready", completed: manifest.recordCount, total: manifest.recordCount, message: `${manifest.recordCount.toLocaleString()} foods available offline` });
    return;
  }

  await requestDurableStorage(manifest.totalBytes);
  let completed = 0;
  for (const shard of manifest.shards) {
    const installedKey = `installed:${manifest.catalogVersion}:${shard.file}`;
    if ((await getCatalogMeta<string>(installedKey)) === shard.sha256) {
      completed += shard.records;
      onProgress?.({ status: "installing", completed, total: manifest.recordCount, message: `Resuming catalog at ${completed.toLocaleString()} / ${manifest.recordCount.toLocaleString()}` });
      continue;
    }
    onProgress?.({ status: "downloading", completed, total: manifest.recordCount, message: `Downloading ${shard.file}…` });
    const response = await fetch(`/food-data/${encodeURIComponent(shard.file)}`);
    if (!response.ok) throw new Error(`Food catalog download failed at ${shard.file}.`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    if ((await sha256(bytes)) !== shard.sha256) throw new Error(`Food catalog checksum failed for ${shard.file}.`);
    const foods = JSON.parse(new TextDecoder().decode(bytes)) as unknown[];
    const valid = foods.filter(validateFoodDefinition) as FoodDefinition[];
    if (valid.length !== foods.length || valid.length !== shard.records) throw new Error(`Food catalog validation failed for ${shard.file}.`);
    for (let index = 0; index < valid.length; index += 500) {
      await putFoodBatch(manifest.catalogVersion, valid.slice(index, index + 500));
      completed += Math.min(500, valid.length - index);
      onProgress?.({ status: "installing", completed, total: manifest.recordCount, message: `Installing foods ${completed.toLocaleString()} / ${manifest.recordCount.toLocaleString()}` });
    }
    await setCatalogMeta(installedKey, shard.sha256);
  }

  const previous = activeVersion;
  await setCatalogMeta("activeCatalog", manifest.catalogVersion);
  await setCatalogMeta("catalogManifest", manifest);
  activeVersion = manifest.catalogVersion;
  if (previous !== "core-1" && previous !== activeVersion) await deleteCatalogVersion(previous);
  onProgress?.({ status: "ready", completed, total: manifest.recordCount, message: `${completed.toLocaleString()} foods available offline` });
}

async function requestDurableStorage(totalBytes: number): Promise<void> {
  if (navigator.storage?.persist) await navigator.storage.persist();
  if (!navigator.storage?.estimate) return;
  const { quota = 0, usage = 0 } = await navigator.storage.estimate();
  if (quota && quota - usage < totalBytes * 2) throw new Error("Not enough browser storage for the full catalog; core foods remain available.");
}

function validateManifest(manifest: CatalogManifest): void {
  if (!manifest || manifest.schemaVersion !== 1 || !manifest.catalogVersion || !Array.isArray(manifest.shards)) {
    throw new Error("Food catalog manifest is invalid.");
  }
  if (manifest.shards.some((shard) => !shard.file || shard.bytes > 20 * 1024 * 1024 || !shard.sha256)) {
    throw new Error("Food catalog contains an invalid or oversized shard.");
  }
}

function dedupe(foods: FoodDefinition[]): FoodDefinition[] {
  return [...new Map(foods.map((food) => [food.id, food])).values()];
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const input = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const digest = await crypto.subtle.digest("SHA-256", input);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export function createCustomFood(input: {
  name: string;
  servingGrams: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}): FoodDefinition {
  const name = input.name.trim();
  const servingGrams = Number(input.servingGrams);
  if (!name || name.length > 120) throw new Error("Enter a food name up to 120 characters.");
  if (!Number.isFinite(servingGrams) || servingGrams <= 0 || servingGrams > 5000) throw new Error("Enter a valid serving weight in grams.");
  const servingMacros = [input.calories, input.protein, input.carbs, input.fat].map(Number);
  if (servingMacros.some((value) => !Number.isFinite(value) || value < 0)) throw new Error("Macros must be zero or greater.");
  const factor = 100 / servingGrams;
  const scalePrecisely = (value: number) => Math.round(value * factor * 10000) / 10000;
  const id = `custom:${crypto.randomUUID()}`;
  const searchText = normalizeSearchText(name);
  return {
    id,
    provider: "CUSTOM",
    providerId: id.slice(7),
    catalogVersion: "custom-1",
    name,
    description: `${servingGrams} g labelled serving`,
    dataType: "Custom food",
    per100g: {
      calories: scalePrecisely(input.calories),
      protein: scalePrecisely(input.protein),
      carbs: scalePrecisely(input.carbs),
      fat: scalePrecisely(input.fat)
    },
    portions: [{ label: "labelled serving", grams: servingGrams }],
    searchText,
    tokens: searchTokens(searchText)
  };
}
